import { useMemo } from 'react';
import { create } from 'zustand';
import type {
  AgentAvailability,
  AgentProfile,
  PromptTemplate,
  SessionInfo,
  UserInfo,
  Workspace,
} from '@cm/protocol';
import { api, builtInDaemonUrl, setActiveHostForApi } from './api';
import {
  hasValidToken,
  hostLabel,
  loadActiveHostId,
  loadHosts,
  removeHost as removeFromList,
  saveActiveHostId,
  saveHosts,
  upsertHost,
  type Host,
} from './hosts';
import {
  emptyLayout,
  loadLayout,
  reconcile,
  saveLayout,
  type WorkspaceLayout,
} from './layout';
import {
  applyFontPrefs,
  applyTheme,
  loadFontPrefs,
  loadSidebarPinned,
  loadTheme,
  saveFontPrefs,
  saveSidebarPinned,
  saveTheme,
  type FontPrefs,
  type Theme,
} from './theme';

export interface Toast {
  id: number;
  kind: 'info' | 'error' | 'warn' | 'success';
  text: string;
}

export type PanelName = 'prompts' | 'profiles' | 'new-session' | 'appearance' | null;
export type LayoutMode = 'grid' | 'focus';

interface State {
  // --- hosts ---
  /** Servidores conhecidos. O app nao tem "o" servidor: tem uma lista. */
  hosts: Host[];
  activeHostId: string | null;

  // --- sessao do usuario ---
  user: UserInfo | null;
  authError: string | null;
  connected: boolean;

  // --- dados do daemon ---
  sessions: SessionInfo[];
  workspaces: Workspace[];
  profiles: AgentProfile[];
  prompts: PromptTemplate[];
  agents: AgentAvailability[];

  // --- estado de UI ---
  /** Projeto cuja aba esta aberta; filtra os panes visiveis. */
  activeWorkspaceId: string | null;
  /** Layout do mosaico por projeto, carregado do localStorage sob demanda. */
  layouts: Record<string, WorkspaceLayout>;
  focusedId: string | null;
  /** Sessoes marcadas para receber broadcast. */
  selectedIds: string[];
  layout: LayoutMode;
  panel: PanelName;
  toasts: Toast[];
  /**
   * Tema ativo. Fica no estado -- e nao so no <html> -- porque o xterm nao le
   * CSS: o TerminalPane precisa dos valores em hex para montar o tema dele.
   */
  theme: Theme;
  /**
   * Tamanho de fonte do chrome e do terminal. Fica no estado, e nao so no CSS,
   * porque o xterm nao le CSS: a fonte dele e opcao em JS.
   */
  fonts: FontPrefs;
  /**
   * Sidebar travada aberta. Quando falsa ela fica recolhida numa faixa e so
   * abre no hover -- sobrepondo os panes, nunca empurrando (ver styles.css).
   */
  sidebarPinned: boolean;

  // --- acoes de host ---
  initHosts: () => void;
  saveHost: (host: Host) => void;
  deleteHost: (id: string) => void;
  switchHost: (id: string) => void;
  /** Guarda o token apos o login e marca o host como autenticado. */
  authenticate: (hostId: string, token: string, expiresAt: string, username: string) => void;

  // --- acoes ---
  setUser: (user: UserInfo | null) => void;
  setConnected: (connected: boolean) => void;
  logout: (reason?: string) => void;

  setSessions: (sessions: SessionInfo[]) => void;
  upsertSession: (session: SessionInfo) => void;
  markSessionClosed: (id: string, exitCode: number | null) => void;
  removeSession: (id: string) => void;

  setWorkspaces: (workspaces: Workspace[]) => void;
  /** Aplica uma transformacao no layout do projeto e persiste. */
  updateLayout: (workspaceId: string, fn: (l: WorkspaceLayout) => WorkspaceLayout) => void;
  /** Garante que o layout existe e reflete as sessoes atuais. */
  syncLayout: (workspaceId: string, sessionIds: string[]) => void;
  setActiveWorkspace: (id: string | null) => void;
  setProfiles: (profiles: AgentProfile[]) => void;
  setPrompts: (prompts: PromptTemplate[]) => void;
  setAgents: (agents: AgentAvailability[]) => void;

  focus: (id: string | null) => void;
  toggleSelected: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setLayout: (layout: LayoutMode) => void;
  openPanel: (panel: PanelName) => void;
  /** Troca o tema: aplica no documento na hora e persiste com debounce. */
  setTheme: (theme: Theme) => void;
  /** Troca o tamanho de fonte. Mesmo esquema de aplicar ja, gravar depois. */
  setFonts: (fonts: FontPrefs) => void;
  toggleSidebarPinned: () => void;

  pushToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: number) => void;

  refreshAll: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  refreshProfiles: () => Promise<void>;
  refreshPrompts: () => Promise<void>;
}

let toastSeq = 0;
let themeSaveTimer: number | null = null;
let fontsSaveTimer: number | null = null;

/** Estado que pertence a um host e precisa ser descartado ao trocar dele. */
function blankSession() {
  return {
    user: null,
    sessions: [],
    workspaces: [],
    profiles: [],
    prompts: [],
    agents: [],
    activeWorkspaceId: null,
    layouts: {},
    focusedId: null,
    selectedIds: [],
    connected: false,
    panel: null as PanelName,
  };
}

export const useStore = create<State>((set, get) => ({
  hosts: [],
  activeHostId: null,

  user: null,
  authError: null,
  connected: false,

  sessions: [],
  workspaces: [],
  profiles: [],
  prompts: [],
  agents: [],

  activeWorkspaceId: null,
  layouts: {},
  focusedId: null,
  selectedIds: [],
  layout: 'grid',
  panel: null,
  toasts: [],
  // O main.tsx ja aplicou o tema no <html> antes do render; aqui so espelhamos
  // o mesmo valor no estado. Note que `theme` NAO entra no blankSession(): e
  // preferencia da maquina, nao do host nem da sessao, e trocar de servidor ou
  // sair nao devia repintar a interface.
  theme: loadTheme(),
  fonts: loadFontPrefs(),
  sidebarPinned: loadSidebarPinned(),

  /**
   * Idempotente de proposito. O daemon reenvia o usuario no `hello` de cada
   * conexao; se isso trocasse a identidade do objeto, o efeito que abre o
   * WebSocket reagiria e reconectaria -- um laco infinito de reconexao.
   */
  /**
   * Carrega a lista de hosts (migrando a config antiga, se houver) e informa
   * o host ativo para a camada de API. Chamado uma vez, na montagem do App.
   */
  initHosts: () => {
    const hosts = loadHosts(builtInDaemonUrl());
    const activeHostId = loadActiveHostId(hosts);
    saveHosts(hosts);
    saveActiveHostId(activeHostId);
    const active = hosts.find((h) => h.id === activeHostId) ?? null;
    setActiveHostForApi(active);
    set({ hosts, activeHostId });
  },

  saveHost: (host) =>
    set((state) => {
      const hosts = upsertHost(state.hosts, host);
      saveHosts(hosts);
      if (host.id === state.activeHostId) setActiveHostForApi(host);
      return { hosts };
    }),

  deleteHost: (id) =>
    set((state) => {
      const hosts = removeFromList(state.hosts, id);
      saveHosts(hosts);
      // Remover o host ativo exige eleger outro, senao o app fica sem destino.
      const activeHostId = state.activeHostId === id ? (hosts[0]?.id ?? null) : state.activeHostId;
      saveActiveHostId(activeHostId);
      setActiveHostForApi(hosts.find((h) => h.id === activeHostId) ?? null);
      return { hosts, activeHostId, ...(state.activeHostId === id ? blankSession() : {}) };
    }),

  switchHost: (id) =>
    set((state) => {
      const host = state.hosts.find((h) => h.id === id);
      if (!host) return {};
      saveActiveHostId(id);
      setActiveHostForApi(host);
      // Trocar de host troca TUDO: sessoes, projetos, perfis e prompts vivem
      // no daemon. Limpar aqui evita mostrar dados do host anterior enquanto
      // os novos carregam.
      return { activeHostId: id, ...blankSession() };
    }),

  authenticate: (hostId, token, expiresAt, username) =>
    set((state) => {
      const host = state.hosts.find((h) => h.id === hostId);
      if (!host) return {};
      const updated: Host = { ...host, token, tokenExpiresAt: expiresAt, username };
      const hosts = upsertHost(state.hosts, updated);
      saveHosts(hosts);
      setActiveHostForApi(updated);
      return { hosts };
    }),

  setUser: (user) =>
    set((state) => {
      const same =
        state.user && user && state.user.id === user.id && state.user.username === user.username;
      return same ? { authError: null } : { user, authError: null };
    }),
  setConnected: (connected) => set({ connected }),

  logout: (reason) =>
    set((state) => {
      // Esquece o token apenas do host ativo: sair de uma VPS nao deve
      // desconectar das outras.
      const host = state.hosts.find((h) => h.id === state.activeHostId);
      let hosts = state.hosts;
      if (host) {
        const cleared: Host = { ...host, token: null, tokenExpiresAt: null };
        hosts = upsertHost(hosts, cleared);
        saveHosts(hosts);
        setActiveHostForApi(cleared);
      }
      return { hosts, authError: reason ?? null, ...blankSession() };
    }),

  setSessions: (sessions) =>
    set((state) => {
      const stillThere = state.focusedId && sessions.some((s) => s.id === state.focusedId);
      // O foco tem que cair numa sessao da aba aberta. Pegar sessions[0] as
      // cegas focaria uma sessao de outro projeto, invisivel na tela.
      const inActiveTab = sessions.filter(
        (s) => state.activeWorkspaceId === null || s.workspaceId === state.activeWorkspaceId,
      );
      return {
        sessions,
        focusedId: stillThere ? state.focusedId : (inActiveTab[0]?.id ?? null),
        selectedIds: state.selectedIds.filter((id) => sessions.some((s) => s.id === id)),
      };
    }),

  upsertSession: (session) =>
    set((state) => {
      const index = state.sessions.findIndex((s) => s.id === session.id);
      const sessions =
        index === -1
          ? [...state.sessions, session]
          : state.sessions.map((s) => (s.id === session.id ? session : s));
      return { sessions, focusedId: state.focusedId ?? session.id };
    }),

  markSessionClosed: (id, exitCode) =>
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, status: 'exited' as const, exitCode, pid: null } : s,
      ),
    })),

  removeSession: (id) =>
    set((state) => {
      const sessions = state.sessions.filter((s) => s.id !== id);
      return {
        sessions,
        focusedId: state.focusedId === id ? (sessions[0]?.id ?? null) : state.focusedId,
        selectedIds: state.selectedIds.filter((s) => s !== id),
      };
    }),

  setWorkspaces: (workspaces) =>
    set((state) => ({
      workspaces,
      // Mantem a aba atual se ela ainda existe; senao cai no primeiro projeto
      // ativo, para a interface nunca ficar sem aba selecionada.
      activeWorkspaceId:
        state.activeWorkspaceId && workspaces.some((w) => w.id === state.activeWorkspaceId)
          ? state.activeWorkspaceId
          : (workspaces.find((w) => !w.archived)?.id ?? null),
    })),

  setActiveWorkspace: (id) => set({ activeWorkspaceId: id, selectedIds: [] }),

  updateLayout: (workspaceId, fn) =>
    set((state) => {
      const current = state.layouts[workspaceId] ?? loadLayout(workspaceId);
      const next = fn(current);
      saveLayout(workspaceId, next);
      return { layouts: { ...state.layouts, [workspaceId]: next } };
    }),

  syncLayout: (workspaceId, sessionIds) =>
    set((state) => {
      const current = state.layouts[workspaceId] ?? loadLayout(workspaceId);
      const next = reconcile(current, sessionIds);
      // Comparar antes de gravar evita um laco de render: syncLayout roda a
      // cada mudanca de sessao, e escrever um objeto novo identico faria os
      // componentes reagirem para sempre.
      if (JSON.stringify(next) === JSON.stringify(current) && state.layouts[workspaceId]) {
        return {};
      }
      saveLayout(workspaceId, next);
      return { layouts: { ...state.layouts, [workspaceId]: next } };
    }),

  setProfiles: (profiles) => set({ profiles }),
  setPrompts: (prompts) => set({ prompts }),
  setAgents: (agents) => set({ agents }),

  focus: (id) => set({ focusedId: id }),

  toggleSelected: (id) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(id)
        ? state.selectedIds.filter((s) => s !== id)
        : [...state.selectedIds, id],
    })),

  selectAll: () =>
    set((state) => ({
      // So marca o que esta visivel na aba atual: um broadcast que atinge
      // projetos fora da tela seria uma surpresa desagradavel.
      selectedIds: state.sessions
        .filter(
          (s) =>
            s.status === 'running' &&
            (state.activeWorkspaceId === null || s.workspaceId === state.activeWorkspaceId),
        )
        .map((s) => s.id),
    })),

  clearSelection: () => set({ selectedIds: [] }),
  setLayout: (layout) => set({ layout }),
  openPanel: (panel) => set((state) => ({ panel: state.panel === panel ? null : panel })),

  setTheme: (theme) => {
    // Aplicar e sincrono para o ajuste fino ter preview instantaneo: o estado
    // do painel e a tela passam a ser a mesma coisa, sem um segundo caminho de
    // renderizacao para divergir.
    applyTheme(theme);
    set({ theme });
    // Gravar e que precisa de folga. <input type="color"> emite `input` a cada
    // quadro enquanto o usuario arrasta o seletor; serializar o tema a 60Hz
    // enche o localStorage de escrita descartavel. So o fim do gesto importa.
    if (themeSaveTimer !== null) window.clearTimeout(themeSaveTimer);
    themeSaveTimer = window.setTimeout(() => {
      themeSaveTimer = null;
      saveTheme(theme);
    }, 250);
  },

  toggleSidebarPinned: () =>
    set((state) => {
      const sidebarPinned = !state.sidebarPinned;
      saveSidebarPinned(sidebarPinned);
      return { sidebarPinned };
    }),

  setFonts: (fonts) => {
    applyFontPrefs(fonts);
    set({ fonts });
    // Mesmo motivo do tema: arrastar um slider emite `input` a cada quadro.
    if (fontsSaveTimer !== null) window.clearTimeout(fontsSaveTimer);
    fontsSaveTimer = window.setTimeout(() => {
      fontsSaveTimer = null;
      saveFontPrefs(fonts);
    }, 250);
  },

  pushToast: (toast) => {
    const id = ++toastSeq;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    // Erros ficam na tela mais tempo; o resto e informativo e some rapido.
    const ttl = toast.kind === 'error' ? 9_000 : 4_500;
    window.setTimeout(() => get().dismissToast(id), ttl);
  },

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  refreshSessions: async () => {
    const { sessions } = await api.sessions();
    get().setSessions(sessions);
  },

  refreshWorkspaces: async () => {
    const { workspaces } = await api.workspaces();
    get().setWorkspaces(workspaces);
  },

  refreshProfiles: async () => {
    const { profiles } = await api.profiles();
    set({ profiles });
  },

  refreshPrompts: async () => {
    const { prompts } = await api.prompts();
    set({ prompts });
  },

  refreshAll: async () => {
    const results = await Promise.allSettled([
      api.sessions(),
      api.workspaces(),
      api.profiles(),
      api.prompts(),
      api.agents(),
    ]);
    const [sessions, workspaces, profiles, prompts, agents] = results;
    // setWorkspaces antes de setSessions: ele e quem resolve a aba ativa.
    if (workspaces.status === 'fulfilled') get().setWorkspaces(workspaces.value.workspaces);
    if (sessions.status === 'fulfilled') get().setSessions(sessions.value.sessions);
    if (profiles.status === 'fulfilled') set({ profiles: profiles.value.profiles });
    if (prompts.status === 'fulfilled') set({ prompts: prompts.value.prompts });
    if (agents.status === 'fulfilled') set({ agents: agents.value.agents });

    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      get().pushToast({
        kind: 'error',
        text: `nao consegui carregar ${failed.length} de ${results.length} recursos do daemon`,
      });
    }
  },
}));

/** Seletor util: a sessao em foco. */
export function useFocusedSession(): SessionInfo | null {
  return useStore((s) => s.sessions.find((x) => x.id === s.focusedId) ?? null);
}

/**
 * Sessoes do projeto cuja aba esta aberta.
 *
 * O filtro fica num useMemo, NAO dentro do seletor do zustand. Um seletor que
 * devolve `sessions.filter(...)` cria um array novo a cada leitura; como o
 * zustand compara o resultado com Object.is, ele entende "mudou" sempre e
 * entra em laco infinito de render (React error #185, "maximum update depth").
 * Selecionar as duas fatias cruas -- que sao referencias estaveis -- e derivar
 * depois evita isso.
 */
export function useWorkspaceSessions(): SessionInfo[] {
  const sessions = useStore((s) => s.sessions);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  return useMemo(
    () =>
      activeWorkspaceId === null
        ? sessions
        : sessions.filter((x) => x.workspaceId === activeWorkspaceId),
    [sessions, activeWorkspaceId],
  );
}

/** O projeto da aba aberta. */
export function useActiveWorkspace(): Workspace | null {
  return useStore((s) => s.workspaces.find((w) => w.id === s.activeWorkspaceId) ?? null);
}

/** Layout do projeto aberto, sempre com um valor utilizavel. */
export function useActiveLayout(): WorkspaceLayout {
  const layouts = useStore((s) => s.layouts);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  return useMemo(
    () => (activeWorkspaceId ? (layouts[activeWorkspaceId] ?? emptyLayout()) : emptyLayout()),
    [layouts, activeWorkspaceId],
  );
}

/** Host ativo, ou null se a lista estiver vazia. */
export function useActiveHost(): Host | null {
  const hosts = useStore((s) => s.hosts);
  const activeHostId = useStore((s) => s.activeHostId);
  return useMemo(() => hosts.find((h) => h.id === activeHostId) ?? null, [hosts, activeHostId]);
}

export { hasValidToken, hostLabel };
